import { useMemo, useState } from "react";
import { categoriesAll, feedEvents } from "@/mocks/homeData";

interface Props {
  activeKey: string;
  onFilterChange: (key: string) => void;
}

export default function EventFeed({ activeKey, onFilterChange }: Props) {
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    let base = activeKey === "all" ? feedEvents : feedEvents.filter((e) => e.catKey === activeKey);
    if (keyword.trim()) {
      const k = keyword.trim();
      base = base.filter((e) => e.title.includes(k) || e.excerpt.includes(k) || e.cat.includes(k));
    }
    return base;
  }, [activeKey, keyword]);

  return (
    <section id="feed" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
              <i className="ri-archive-drawer-line" aria-hidden="true"></i>内容专区
            </span>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              热门事件盘点
            </h2>
          </div>
          <p className="text-sm text-foreground-600">
            当前瓜田 <span className="font-bold text-primary-600">{list.length}</span> 篇 · 每日更新
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-1 overflow-x-auto rounded-full border border-background-200 bg-background-100 p-1">
            {categoriesAll.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onFilterChange(c.key)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors sm:text-sm ${
                  activeKey === c.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-50 hover:text-primary-600"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2.5 lg:w-72">
            <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索你想吃的瓜…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setKeyword("")}
                className="text-foreground-400 hover:text-primary-600"
              >
                <i className="ri-close-circle-fill text-base" aria-hidden="true"></i>
              </button>
            )}
          </label>
        </div>

        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-900">这个瓜田暂时空着</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词或栏目，去别处找找大家都在吃什么瓜。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                onFilterChange("all");
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              查看全部盘点
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {list.map((e) => (
              <article
                key={e.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100 transition-colors hover:border-primary-200"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={e.img}
                    alt={e.alt}
                    className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-1 text-xs font-bold text-background-50">
                    {e.cat}
                  </span>
                  <span className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-background-50/85 px-2 py-1 text-xs font-semibold text-foreground-700 backdrop-blur">
                    <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                    {e.read}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {e.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{e.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3">
                    <span className="text-xs text-foreground-500">{e.date} 更新</span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                      围观
                      <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}